// The enquiry form used on the home page, a project page and the contact
// page: name and phone first, the rest optional, one clear button. It posts
// to /api/leads exactly as before (source, UTM tags, project) and says what
// happened in words.
import { useState } from 'react';
import { api, captureUtm } from '../lib/api.js';
import { useSite } from '../lib/site-context.jsx';

const BUDGETS = ['Under ₹50L', '₹50L – ₹1 Cr', '₹1 Cr – ₹3 Cr', '₹3 Cr – ₹8 Cr', 'Above ₹8 Cr'];

export function LeadForm({ id = 'lead-form', msgId = 'lead-form-msg', prefix = 'lf', source = 'Website', projectId, budget = false, messagePlaceholder, defaultMessage = '', submitLabel, successText, extraLink }) {
  const { t } = useSite();
  const [msg, setMsg] = useState({ text: '', tone: '' });
  const [busy, setBusy] = useState(false);
  const [video, setVideo] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    setBusy(true);
    setMsg({ text: '', tone: '' });
    const data = Object.fromEntries(new FormData(form).entries());
    data.source = source;
    if (projectId) data.project_id = projectId;
    Object.assign(data, captureUtm());
    // Only present once a real Turnstile site key is configured.
    if (typeof window.turnstile !== 'undefined') data.turnstile_token = window.turnstile.getResponse();
    try {
      await api('/api/leads', { method: 'POST', body: JSON.stringify(data) });
      setMsg({ text: successText || t('form_thanks', 'Thank you — our team will reach out shortly.'), tone: 'success' });
      form.reset();
      setVideo(false);
    } catch (err) { setMsg({ text: err.message, tone: 'error' }); } finally { setBusy(false); }
  };

  return (
    <form className="form-panel lead-form" id={id} onSubmit={submit}>
      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${prefix}-name`}>{t('label_full_name', 'Full name*')}</label>
          <input id={`${prefix}-name`} name="name" required autoComplete="name" />
        </div>
        <div className="field">
          <label htmlFor={`${prefix}-phone`}>{t('label_phone', 'Phone*')}</label>
          <input id={`${prefix}-phone`} name="phone" type="tel" inputMode="tel" required autoComplete="tel" placeholder="+91 98XXX XXXXX" />
        </div>
        <div className="field">
          <label htmlFor={`${prefix}-email`}>{t('label_email', 'Email')}</label>
          <input id={`${prefix}-email`} name="email" type="email" autoComplete="email" />
        </div>
        {budget && (
          <div className="field">
            <label htmlFor={`${prefix}-budget`}>{t('label_budget', 'Budget range')}</label>
            <select id={`${prefix}-budget`} name="budget_range" defaultValue="">
              <option value="">{t('option_select', 'Select…')}</option>
              {BUDGETS.map((b) => <option key={b}>{b}</option>)}
            </select>
          </div>
        )}
        <div className="field full">
          <label htmlFor={`${prefix}-message`}>{t('label_message', 'Message')}</label>
          <textarea id={`${prefix}-message`} name="message" rows={3} defaultValue={defaultMessage} placeholder={messagePlaceholder || t('placeholder_message', 'Tell us the corridor, project type or timeline you have in mind')} />
        </div>
        <label className="check full" htmlFor={`${prefix}-nri`}>
          <input type="checkbox" id={`${prefix}-nri`} name="is_nri" /> <span>{t('label_nri', "I'm an NRI — I'd prefer a video call")}</span>
        </label>
        <label className="check full" htmlFor={`${prefix}-video-call`}>
          <input type="checkbox" id={`${prefix}-video-call`} name="requested_video_call" checked={video} onChange={(e) => setVideo(e.target.checked)} /> <span>{t('label_video', 'Request a video consultation instead of a phone call')}</span>
        </label>
        {video && (
          <div className="field full" id={`${prefix}-video-datetime-field`}>
            <label htmlFor={`${prefix}-video-datetime`}>{t('label_datetime', 'Preferred date/time')}</label>
            <input id={`${prefix}-video-datetime`} name="preferred_datetime" placeholder="e.g. Weekday evenings after 6pm IST" />
          </div>
        )}
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-rust" disabled={busy}>{busy ? t('form_sending', 'Sending…') : (submitLabel || t('submit_enquiry', 'Submit Enquiry'))}</button>
        {extraLink}
      </div>
      <div className={`form-msg ${msg.tone}`} id={msgId} role="status" aria-live="polite">{msg.text}</div>
    </form>
  );
}
